import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import 'react-loading-skeleton/dist/skeleton.css';
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import Http from "../../Http";
import { Blog_API } from "../../Helper";

const BlogPost = () => {
  const { id } = useParams(); // `id` here is the `content_url`
  const [blog, setBlog] = useState(null);
  const [recentBlogs, setRecentBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        setLoading(true);
        let foundBlog = null;
        
        // First, try to fetch the specific blog directly
        // Using the content_url as a parameter to help your API find it
        const specificData = await Http.get(`${Blog_API}?url=${encodeURIComponent(id)}`);
        
        // Check if we found the blog in this response
        if (specificData && specificData.blogs && specificData.blogs.length > 0) {
          foundBlog = specificData.blogs.find(b => b.content_url === id);
          if (foundBlog) {
            setBlog(foundBlog);
            return; // Exit if found
          }
        }
        
        // If not found, we'll need to search through all pages
        let currentPage = 1;
        const totalPages = Math.ceil(specificData.total_count / 9); // Assuming 9 blogs per page
        
        // Search through each page until we find the blog
        while (currentPage <= totalPages && !foundBlog) {
          // Skip page 1 if we already checked it
          if (currentPage === 1 && specificData.blogs) {
            currentPage++;
            continue;
          }
          
          const pageData = await Http.get(`${Blog_API}?page=${currentPage}`);
          
          if (pageData && pageData.blogs && pageData.blogs.length > 0) {
            foundBlog = pageData.blogs.find(b => b.content_url === id);
            if (foundBlog) {
              setBlog(foundBlog);
              return; // Exit if found
            }
          }
          
          currentPage++;
        }
        
        // If we get here, we couldn't find the blog
        setError("Blog post not found");
        console.error("Blog not found in any page. ID:", id);
        
      } catch (error) {
        console.error("Error fetching blog:", error);
        setError("Failed to load blog post");
      } finally {
        setLoading(false);
      }
    };
    const fetchRecentBlogs = async () => {
      try {
        // Get first page of blogs for recent posts
        const data = await Http.get(`${Blog_API}?page=1`);

        if (data && data.blogs) {
          // Sort recent blogs by date (newest first)
          const sortedRecentBlogs = data.blogs
            .sort((a, b) => new Date(b.content_date) - new Date(a.content_date))
            .slice(0, 10); // Fetch only the first 10 blogs for recent posts

          setRecentBlogs(sortedRecentBlogs);
        }
      } catch (error) {
        console.error("Error fetching recent blogs:", error);
      }
    };

    if (id) {
      fetchBlog();
      fetchRecentBlogs();
    } else {
      setError("Invalid blog URL");
    }
  }, [id]);

  if (loading) {
    return (
      <div className="container mx-auto p-4 py-8">
        <Skeleton height={300} count={3} />
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="container mx-auto p-4 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-500">{error || "Blog post not found"}</h2>
            <p className="mt-4">
              <a href="/blog" className="text-blue-600 hover:underline">
                Return to blog listing
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Only render Helmet if blog is defined */}
      <Helmet>
        <title>{blog.content_meta || blog.content_title}</title>
        <meta name='description' content={blog.content_meta_description || blog.content_title} />
        <meta name='keywords' content={blog.content_keyword} />
          <link
          rel="canonical"
          href={`https://bidbox.ai/blog/s${blog.content_url}`}
        />
        <meta
          name="robots"
          content="max-snippet:-1, max-image-preview:standard"
        />
        <meta name="robots" content="max-video-preview:-1" />
      </Helmet>
      <div className="container mx-auto p-4 py-8">
        <div className="flex flex-wrap px-4">
          <div className="w-full md:w-3/4 pr-6 py-10">
            <motion.img
              src={blog.content_image}
              alt={blog.content_title}
              className="w-full h-60 sm:h-72 md:h-80 lg:h-[28rem] object-contain mb-4 mt-12"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            />
            <motion.h1
              className="text-4xl font-bold mb-4"
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              {blog.content_title}
            </motion.h1>
            <div className="flex items-center text-gray-500 text-sm mb-4">
              <span className="mr-4">
                <i className="fa fa-eye font-bold text-lg">Published : </i> {blog.content_date}
              </span>
            </div>
            <motion.div
              className="text-gray-700 text-base mb-6 blog-content"
              dangerouslySetInnerHTML={{ __html: blog.content_description }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            ></motion.div>
          </div>

          {/* Sidebar */}
          <div className="w-full md:w-1/4 py-10 bg-gray-100 p-4 rounded-lg shadow-lg mt-14">
            <h3 className="text-xl font-bold mb-4 border-b-2 border-blue-600  pb-2">Recent Posts</h3>
            <ul>
              {recentBlogs.map((recentBlog) => (
                <motion.li
                  key={recentBlog.id || recentBlog.content_id}
                  className="mb-4"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <a href={`/blog/${recentBlog.content_url}`} className="flex hover:bg-gray-100 p-2 rounded-lg transition-all duration-300">
                    <img src={recentBlog.content_image} alt={recentBlog.content_title} className="w-16 h-16 object-cover mr-4 rounded-lg" />
                    <div>
                      <h4 className="font-semibold text-gray-800 hover:text-pink-500 transition-colors">{recentBlog.content_title}</h4>
                      <p className="text-gray-500 text-sm">{new Date(recentBlog.content_date).toLocaleDateString()}</p>
                    </div>
                  </a>
                </motion.li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </>
  );
};

export default BlogPost;

// import React, { useState, useEffect } from "react";
// import { useParams } from "react-router-dom";
// import Skeleton from "react-loading-skeleton";
// import 'react-loading-skeleton/dist/skeleton.css';
// import { motion } from "framer-motion";
// import { Helmet } from "react-helmet";
// import Http from "../../Http"; // Import the same Http utility used in BlogMain
// import { Blog_API } from "../../Helper"; // Import the same API endpoint from Helper

// const BlogPost = () => {
//   const { id } = useParams(); // `id` here is the `content_url`
//   const [blog, setBlog] = useState(null);
//   const [recentBlogs, setRecentBlogs] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);

//   useEffect(() => {
//     const fetchBlog = async () => {
//       try {
//         setLoading(true);
//         // Use the same Http utility and API endpoint as BlogMain
//         const data = await Http.get(`${Blog_API}?url=${encodeURIComponent(id)}`);
        
//         // Find the blog with matching content_url
//         const selectedBlog = data.blogs.find((b) => b.content_url === id);
        
//         if (selectedBlog) {
//           setBlog(selectedBlog);
//         } else {
//           setError("Blog post not found");
//         }
//       } catch (error) {
//         console.error("Error fetching blog:", error);
//         setError("Failed to load blog post");
//       } finally {
//         setLoading(false);
//       }
//     };

//     const fetchRecentBlogs = async () => {
//       try {
//         // Use the same Http utility and API endpoint as BlogMain
//         const data = await Http.get(Blog_API);

//         // Sort recent blogs by date (newest first)
//         const sortedRecentBlogs = data.blogs
//           .sort((a, b) => new Date(b.content_date) - new Date(a.content_date))
//           .slice(0, 10); // Fetch only the first 10 blogs for recent posts

//         setRecentBlogs(sortedRecentBlogs);
//       } catch (error) {
//         console.error("Error fetching recent blogs:", error);
//       }
//     };

//     fetchBlog();
//     fetchRecentBlogs();
//   }, [id]);

//   if (loading) {
//     return <Skeleton height={300} count={3} />;
//   }

//   if (error || !blog) {
//     return (
//       <div className="container mx-auto p-4 py-8">
//         <div className="flex justify-center items-center h-64">
//           <div className="text-center">
//             <h2 className="text-2xl font-bold text-red-500">{error || "Blog post not found"}</h2>
//             <p className="mt-4">
//               <a href="/blog" className="text-blue-600 hover:underline">
//                 Return to blog listing
//               </a>
//             </p>
//           </div>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <>
//       {/* Only render Helmet if blog is defined */}
//       <Helmet>
//         <title>{blog.content_meta || blog.content_title}</title>
//         <meta name='description' content={blog.content_meta_description || blog.content_title} />
//         <meta name='keywords' content={blog.content_keyword || ''} />
//       </Helmet>
//       <div className="container mx-auto p-4 py-8">
//         <div className="flex flex-wrap px-4">
//           <div className="w-full md:w-3/4 pr-6 py-10">
//             <motion.img
//               src={blog.content_image}
//               alt={blog.content_title}
//               className="w-full h-60 sm:h-72 md:h-80 lg:h-[28rem] object-contain mb-4 mt-12"
//               initial={{ opacity: 0 }}
//               animate={{ opacity: 1 }}
//               transition={{ duration: 0.5 }}
//             />
//             <motion.h1
//               className="text-4xl font-bold mb-4"
//               initial={{ y: 50, opacity: 0 }}
//               animate={{ y: 0, opacity: 1 }}
//               transition={{ duration: 0.5 }}
//             >
//               {blog.content_title}
//             </motion.h1>
//             <div className="flex items-center text-gray-500 text-sm mb-4">
//               <span className="mr-4">
//                 <i className="fa fa-eye font-bold text-lg">Published : </i> {blog.content_date}
//               </span>
//               {/* <span className="mr-4">
//                 <i className="fa fa-clock"></i> 0 Reads
//               </span>
//               <span>
//                 <i className="fa fa-comments"></i> 0 Comments
//               </span> */}
//             </div>
//             <motion.p
//               className="text-gray-700 text-base mb-6"
//               dangerouslySetInnerHTML={{ __html: blog.content_description }}
//               initial={{ opacity: 0 }}
//               animate={{ opacity: 1 }}
//               transition={{ duration: 0.5 }}
//             ></motion.p>
//           </div>

//           {/* Sidebar */}
//           <div className="w-full md:w-1/4 py-10 bg-gray-100 p-4 rounded-lg shadow-lg mt-14">
//             <h3 className="text-xl font-bold mb-4 border-b-2 border-pink-500 pb-2">Recent Posts</h3>
//             <ul>
//               {recentBlogs.map((recentBlog) => (
//                 <motion.li
//                   key={recentBlog.id || recentBlog.content_id}
//                   className="mb-4"
//                   initial={{ opacity: 0, x: -30 }}
//                   animate={{ opacity: 1, x: 0 }}
//                   transition={{ duration: 0.5 }}
//                 >
//                   <a href={`/blog/${recentBlog.content_url}`} className="flex hover:bg-gray-100 p-2 rounded-lg transition-all duration-300">
//                     <img src={recentBlog.content_image} alt={recentBlog.content_title} className="w-16 h-16 object-cover mr-4 rounded-lg" />
//                     <div>
//                       <h4 className="font-semibold text-gray-800 hover:text-pink-500 transition-colors">{recentBlog.content_title}</h4>
//                       <p className="text-gray-500 text-sm">{new Date(recentBlog.content_date).toLocaleDateString()}</p>
//                     </div>
//                   </a>
//                 </motion.li>
//               ))}
//             </ul>
//           </div>
//         </div>
//       </div>
//     </>
//   );
// };

// export default BlogPost;

// import React, { useState, useEffect } from "react";
// import { useParams } from "react-router-dom";
// import Skeleton from "react-loading-skeleton";
// import 'react-loading-skeleton/dist/skeleton.css';
// import { motion } from "framer-motion"; // For animations
// import { Helmet } from "react-helmet";

// const BlogPost = () => {
//   const { id } = useParams(); // `id` here is actually the `content_url`
//   const [blog, setBlog] = useState(null);
//   const [recentBlogs, setRecentBlogs] = useState([]);
//   const [loading, setLoading] = useState(true); // Loading state

//   useEffect(() => {
//     const fetchBlog = async () => {
//       const response = await fetch("https://zonemedia.co/bidbox-blog/react-app-directory/blog-api.php");
//       const data = await response.json();
//       const selectedBlog = data.blogs.find((b) => b.content_url === id); // Find by `content_url`
//       setBlog(selectedBlog);
//       setLoading(false); // Stop loading when data is fetched
//     };

//     const fetchRecentBlogs = async () => {
//       const response = await fetch("https://zonemedia.co/bidbox-blog/react-app-directory/blog-api.php");
//       const data = await response.json();

//       // Sort recent blogs by date (newest first)
//       const sortedRecentBlogs = data.blogs
//         .sort((a, b) => new Date(b.content_date) - new Date(a.content_date))
//         .slice(0, 10); // Fetch only the first 3 blogs for recent posts

//       setRecentBlogs(sortedRecentBlogs);
//     };

//     fetchBlog();
//     fetchRecentBlogs();
//   }, [id]);

//   if (loading) {
//     return <Skeleton height={300} count={3} />;
//   }

//   return (
//     <>
//     <Helmet>
//         <title>{blog.content_meta}</title>
//         <meta name='description' content={blog.content_meta_description} />
//         <meta name='keywords' content={blog.content_keyword} />
//       </Helmet>
//     <div className="container mx-auto p-4 py-8">
//       <div className="flex flex-wrap px-4">
//         <div className="w-full md:w-3/4 pr-6 py-10">
//           <motion.img
//             src={blog.content_image}
//             alt={blog.content_title}
//             className="w-full h-60 sm:h-72 md:h-80 lg:h-[28rem] object-contain mb-4 mt-12"
//             initial={{ opacity: 0 }}
//             animate={{ opacity: 1 }}
//             transition={{ duration: 0.5 }}
//           />
//           <motion.h1
//             className="text-4xl font-bold mb-4"
//             initial={{ y: 50, opacity: 0 }}
//             animate={{ y: 0, opacity: 1 }}
//             transition={{ duration: 0.5 }}
//           >
//             {blog.content_title}
//           </motion.h1>
//           <div className="flex items-center text-gray-500 text-sm mb-4">
//             <span className="mr-4">
//               <i className="fa fa-eye font-bold text-lg">Published : </i> {blog.content_date}
//             </span>
//             {/* <span className="mr-4">
//               <i className="fa fa-clock"></i> 0 Reads
//             </span>
//             <span>
//               <i className="fa fa-comments"></i> 0 Comments
//             </span> */}
//           </div>
//           <motion.p
//             className="text-gray-700 text-base mb-6"
//             dangerouslySetInnerHTML={{ __html: blog.content_description }}
//             initial={{ opacity: 0 }}
//             animate={{ opacity: 1 }}
//             transition={{ duration: 0.5 }}
//           ></motion.p>
//         </div>

//         {/* Sidebar */}
//         <div className="w-full md:w-1/4 py-10 bg-gray-100 p-4 rounded-lg shadow-lg mt-14">
//           <h3 className="text-xl font-bold mb-4 border-b-2 border-pink-500 pb-2">Recent Posts</h3>
//           <ul>
//             {recentBlogs.map((recentBlog) => (
//               <motion.li
//                 key={recentBlog.id}
//                 className="mb-4"
//                 initial={{ opacity: 0, x: -30 }}
//                 animate={{ opacity: 1, x: 0 }}
//                 transition={{ duration: 0.5 }}
//               >
//                 <a href={`/blog/${recentBlog.content_url}`} className="flex hover:bg-gray-100 p-2 rounded-lg transition-all duration-300">
//                   <img src={recentBlog.content_image} alt={recentBlog.content_title} className="w-16 h-16 object-cover mr-4 rounded-lg" />
//                   <div>
//                     <h4 className="font-semibold text-gray-800 hover:text-pink-500 transition-colors">{recentBlog.content_title}</h4>
//                     <p className="text-gray-500 text-sm">{new Date(recentBlog.content_date).toLocaleDateString()}</p>
//                   </div>
//                 </a>
//               </motion.li>
//             ))}
//           </ul>
//         </div>
//       </div>
//     </div>
//     </>
//   );
// };

// export default BlogPost;



